import { api } from "./api";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string | null;
  senderClientId?: string | null;
  senderLabel?: string | null;
  content: string;
  createdAt: string;
  reactions?: Array<{
    emoji: string;
    count: number;
    reactedByMe: boolean;
  }>;
  status?: "sending";
}

export type ConversationStatus = "OPEN" | "CLOSURE_PENDING" | "CLOSED";

export interface ConversationListItem {
  id: string;
  subject: string;
  status: ConversationStatus;
  createdAt: string;
  updatedAt: string;
  peerLabel?: string;
  /** Profil client du fil (requis en mode multi-pros pour filtrer « à traiter »). */
  clientId?: string | null;
  org?: { id: string; name: string } | null;
  lastMessage: ChatMessage | null;
  seenAt?: string | null;
  hasUnread?: boolean;
}

export interface ConversationSummary {
  id: string;
  subject: string;
  status: ConversationStatus;
  peerLabel?: string;
  hasClientParticipant: boolean;
  org?: {
    id: string;
    name: string;
    logo: string | null;
    profilePhoto: string | null;
  } | null;
}

export interface SendMessagePayload {
  content: string;
}

export interface ToggleReactionPayload {
  emoji: string;
}



export const fetchPortalConversations = async (): Promise<ConversationListItem[]> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode ? "/portal/aggregate/conversations" : "/portal/conversations";
  const { data } = await api.get<ConversationListItem[]>(url);
  return data;
}



export const createPortalConversation = async (subject: string, orgId?: string, initialMessage?: string): Promise<{ id: string }> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode
    ? "/portal/aggregate/conversations"
    : "/portal/conversations";
  const body = isAllMode
    ? { orgId, subject, initialMessage }
    : { subject, initialMessage };
  const { data } = await api.post<{ id: string }>(url, body);
  return data;
}



export const fetchPortalConversationSummary = async (conversationId: string): Promise<ConversationSummary> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode
    ? `/portal/aggregate/conversations/${conversationId}`
    : `/portal/conversations/${conversationId}`;
  const { data } = await api.get<ConversationSummary>(url);
  return data;
}



export const postPortalClosureResponse = async (conversationId: string, accepted: boolean): Promise<{ ok: boolean; status: ConversationStatus }> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode
    ? `/portal/aggregate/conversations/${conversationId}/closure-response`
    : `/portal/conversations/${conversationId}/closure-response`;
  const { data } = await api.post<{ ok: boolean; status: ConversationStatus }>(
    url,
    { accepted },
  );
  return data;
}



export const postPortalCloseConversation = async (conversationId: string): Promise<{ ok: boolean; status: "CLOSED" }> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode
    ? `/portal/aggregate/conversations/${conversationId}/close`
    : `/portal/conversations/${conversationId}/close`;
  const { data } = await api.post<{ ok: boolean; status: "CLOSED" }>(url);
  return data;
}



export const fetchPortalMessages = async (conversationId: string, limit: number = 100): Promise<ChatMessage[]> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode
    ? `/portal/aggregate/conversations/${conversationId}/messages`
    : `/portal/conversations/${conversationId}/messages`;
  const { data } = await api.get<ChatMessage[]>(
    url,
    { params: { limit } },
  );
  return data;
}

/** Long-poll serveur : une seule requête à la fois ; timeout réduit vs 25s pour libérer les slots HTTP plus vite. */
const PORTAL_POLL_TIMEOUT_MS = 20_000;



export const pollPortalMessages = async (conversationId: string, after?: string, options?: { signal?: AbortSignal }): Promise<{ hasUpdates: boolean; updatedAt: string }> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode
    ? `/portal/aggregate/conversations/${conversationId}/messages/poll`
    : `/portal/conversations/${conversationId}/messages/poll`;
  const { data } = await api.get<{ hasUpdates: boolean; updatedAt: string }>(url, {
    params: { after, timeoutMs: PORTAL_POLL_TIMEOUT_MS },
    signal: options?.signal,
  });
  return data;
}



export const postPortalMessage = async (conversationId: string, payload: SendMessagePayload): Promise<ChatMessage> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode
    ? `/portal/aggregate/conversations/${conversationId}/messages`
    : `/portal/conversations/${conversationId}/messages`;
  const { data } = await api.post<ChatMessage>(
    url,
    payload,
  );
  return data;
}



export const addPortalMessageReaction = async (conversationId: string, messageId: string, payload: ToggleReactionPayload): Promise<{ ok: boolean }> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode
    ? `/portal/aggregate/conversations/${conversationId}/messages/${messageId}/reactions`
    : `/portal/conversations/${conversationId}/messages/${messageId}/reactions`;
  const { data } = await api.post<{ ok: boolean }>(url, payload);
  return data;
}



export const removePortalMessageReaction = async (conversationId: string, messageId: string, payload: ToggleReactionPayload): Promise<{ ok: boolean }> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const path = isAllMode
    ? `/portal/aggregate/conversations/${conversationId}/messages/${messageId}/reactions`
    : `/portal/conversations/${conversationId}/messages/${messageId}/reactions`;
  const { data } = await api.delete<{ ok: boolean }>(
    `${path}?emoji=${encodeURIComponent(payload.emoji)}`,
  );
  return data;
}



export const postPortalConversationSeen = async (conversationId: string): Promise<{ ok: boolean; conversationId: string; seenAt: string }> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode
    ? `/portal/aggregate/conversations/${conversationId}/seen`
    : `/portal/conversations/${conversationId}/seen`;
  const { data } = await api.post<{
    ok: boolean;
    conversationId: string;
    seenAt: string;
  }>(url);
  return data;
}



export const postPortalConversationsSeenAll = async (): Promise<{
  ok: boolean;
  count: number;
  seenAt: string;
}> => {
  const isAllMode = useOrgStore.getState().activeClientId === ALL_CLIENTS_KEY;
  const url = isAllMode
    ? "/portal/aggregate/conversations/seen-all"
    : "/portal/conversations/seen-all";
  const { data } = await api.post<{ ok: boolean; count: number; seenAt: string }>(
    url,
  );
  return data;
}
